import moment from 'moment';
import React, { useMemo, useState, useCallback, useContext } from 'react';
import styled from 'styled-components';
import { getDurationFromMilliseconds, getTimeRangeWithDates } from '@ubeya/shared/utils/time';
import { FlexColumn, SmallBody, Small, Tooltip } from '@ubeya/shared-web/components';
import { ReactComponent as BaseWarningIcon } from '@ubeya/shared-web/assets/warning-yellow.svg';
import ConfigContext from '@ubeya/shared-web/contexts/ConfigContext';
import { ALPHA_ACCOUNT_ID, MADRID_LEGENDS_ACCOUNT_ID } from '@ubeya/shared/utils/accounts';
import { isReadOnly, totalTime } from 'shared/utils/timesheet';
import { API_DATE_FORMAT, API_DATE_TIME_FORMAT } from '../../../../../../packages/shared/constants';
import TotalTimeModal from './TotalTimeModal';
import BreakWarningIndicator from './BreakWarningIndicator';

const Wrapper = styled(FlexColumn)`
  position: relative;

  > ${SmallBody} {
    font-weight: 500;
  }

  > ${Small} {
    color: ${({ theme }) => theme.colors.grey3};
  }
`;

const WarningIcon = styled(BaseWarningIcon)`
  margin-left: 5px;
  margin-bottom: -3px;
`;

const ZeroTotal = styled(Small)`
  padding: 5px;
`;

const TotalTimeRenderer = ({ rowData, columnData }) => {
  const {
    summarized,
    subRows,
    actualTotal,
    scheduleTotal,
    scheduleIn,
    scheduleOut,
    employee,
    dummy,
    accountId,
    isExternal,
    breakWarnings
  } = rowData;
  const { firstName, lastName } = employee || {};
  const { updateEndTime, updateStartTime, readOnly: generalReadOnly = false } = columnData || {};
  const [showCustomHours, setShowCustomHours] = useState(false);
  const actualTotalDuration = useMemo(() => (actualTotal > 0 ? getDurationFromMilliseconds(actualTotal) : ''), [
    actualTotal
  ]);
  const { t } = useContext(ConfigContext);

  const scheduleDuration = useMemo(
    () => getDurationFromMilliseconds(scheduleTotal ?? totalTime(scheduleIn, scheduleOut)),
    [scheduleIn, scheduleOut, scheduleTotal]
  );
  const actualHours = useMemo(() => actualTotal / 1000 / 60 / 60, [actualTotal]);

  const handleUpdateTimeSheet = useCallback(
    ({ startTime, endTime }) => {
      if (!startTime || !endTime) {
        return;
      }
      const { startTime: newStartTime, endTime: newEndTime } = getTimeRangeWithDates({
        date: rowData.date,
        startTime,
        endTime
      });

      updateStartTime({ newTime: newStartTime, ...rowData });
      updateEndTime({ newTime: newEndTime, ...rowData });
    },
    [rowData, updateEndTime, updateStartTime]
  );

  const setCustomNumberSubmit = useCallback(
    ({ hours }) => {
      setShowCustomHours(false);
      const startTime = (() => {
        if (rowData?.actualIn) {
          return moment(rowData.actualIn, API_DATE_TIME_FORMAT);
        }
        if (rowData?.scheduleIn) {
          return moment(rowData.scheduleIn, API_DATE_TIME_FORMAT);
        }
        return moment(rowData.date, API_DATE_FORMAT).add(10, 'hours');
      })();

      const endTime = (() => {
        return moment(startTime).add(hours, 'hours');
      })();
      handleUpdateTimeSheet({ startTime, endTime });
    },
    [handleUpdateTimeSheet, rowData.actualIn, rowData.date, rowData?.scheduleIn]
  );

  const readOnly = isReadOnly(rowData, generalReadOnly);

  const allowSetCustomHours = useMemo(
    () => !dummy && !readOnly && !!updateStartTime && !!updateEndTime && !subRows && !summarized && !isExternal,
    [dummy, readOnly, updateStartTime, updateEndTime, subRows, summarized, isExternal]
  );

  const onClick = useCallback(() => (allowSetCustomHours ? setShowCustomHours(true) : undefined), [
    allowSetCustomHours
  ]);

  if (subRows && !actualTotal) {
    return null;
  }

  const exceedHours = accountId === ALPHA_ACCOUNT_ID ? 8 : accountId === MADRID_LEGENDS_ACCOUNT_ID ? 10 : 12;

  return (
    <Wrapper onClick={onClick}>
      <SmallBody className="actual">
        <BreakWarningIndicator breakWarnings={breakWarnings} />
        <Tooltip body={t('clickToSetShiftLength')} disabled={!allowSetCustomHours}>
          <span>{actualTotalDuration}</span>
        </Tooltip>

        {!subRows && actualHours >= exceedHours && !summarized && (
          <Tooltip body={t('timesheetTotalHoursExceeded', { total: exceedHours })}>
            <WarningIcon />
          </Tooltip>
        )}
      </SmallBody>

      {actualTotal > 0 && scheduleIn && scheduleOut && (
        <Small className="schedule">{getDurationFromMilliseconds(scheduleDuration)}</Small>
      )}
      {actualTotal === 0 && <ZeroTotal className="schedule">-</ZeroTotal>}

      {allowSetCustomHours && showCustomHours && (
        <TotalTimeModal
          title={t('setCustomHoursTitle', { fullName: `${firstName} ${lastName}` })}
          onClose={() => setShowCustomHours(false)}
          onConfirm={setCustomNumberSubmit}
        />
      )}
    </Wrapper>
  );
};

export default TotalTimeRenderer;
